// Routed pages render a shell (DetailPage/Page/NewPage) instead of composing
// Paper directly — see src/components/layout/AGENTS.md.
import { type ReactNode, type Ref, useContext } from "react";

import { type BoxProps, Column, type PaddingProps } from "@hightouch/htui";

import { PageSuspense } from "src/components/loading";
import { PageRailContext } from "src/components/page-rail";

/** The gap between the card and a rail beside it, which the rails' own spacing derives from. */
export const PAPER_RIGHT_PADDING = 2;
export const PAPER_RIGHT_PADDING_PX = PAPER_RIGHT_PADDING * 4;

/** The card's gutter where it meets the window's right edge, matching its left one. */
const PAPER_EDGE_PADDING = 0.5;

export const Paper = ({
  header,
  topBar,
  children,
  // If the paper is nested within another paper, we need to adjust the padding.
  isNested = false,
  isInset = false,
  scrollRef,
  ...props
}: {
  header?: ReactNode;
  /** The card's first row, outside its suspense boundary, so it stays put while the content below it loads. */
  topBar?: ReactNode;
  isNested?: boolean;
  /** The card holds the window's height, inset from its bottom edge as from the others, rather than running off it. */
  isInset?: boolean;
  /** Makes the card scroll its own content, top bar and all, in place of the window. */
  scrollRef?: Ref<HTMLDivElement>;
} & Omit<BoxProps, keyof PaddingProps>) => {
  const isBesideRail = useContext(PageRailContext);

  const content = (
    <>
      {topBar}
      {topBar ? (
        // Its own stacking context, so nothing in the page, like a tab's indicator, paints over the sticky bar.
        <Column flex={1} minHeight={0} isolation="isolate">
          <PageSuspense>{children}</PageSuspense>
        </Column>
      ) : (
        <PageSuspense>{children}</PageSuspense>
      )}
    </>
  );
  return (
    <Column
      gap={2}
      pb={isInset ? 2 : 0}
      pl={isNested ? 2 : 0.5}
      pr={isBesideRail ? PAPER_RIGHT_PADDING : PAPER_EDGE_PADDING}
      pt={2}
      minWidth={0}
      // Scope for useIdle in editor-overlay.tsx — events outside Paper
      // (e.g. global nav clicks) don't reset the idle timer.
      data-idle-scope
      {...props}
    >
      {header}
      <CardScrollArea
        scrollRef={scrollRef}
        bg="white"
        borderTopRadius="2xl"
        borderBottomRadius={isInset ? "2xl" : undefined}
        boxShadow="sm"
        {...(!scrollRef && { overflow: "clip" })}
      >
        {content}
      </CardScrollArea>
    </Column>
  );
};

/**
 * What scrolls a page card's content in place of the window, or where the window scrolls a box that passes the card's
 * height on, so switching between the two never remounts the page.
 */
export const CardScrollArea = ({
  scrollRef,
  children,
  ...props
}: {
  scrollRef?: Ref<HTMLDivElement>;
  children: ReactNode;
} & BoxProps) => {
  const isScroller = scrollRef !== undefined;
  return (
    <Column
      ref={scrollRef}
      flex={1}
      minHeight={0}
      overflowX={isScroller ? "hidden" : undefined}
      overflowY={isScroller ? "auto" : undefined}
      overscrollBehavior={isScroller ? "none" : undefined}
      // Reserved while nothing scrolls too, so a scrollbar appearing never moves the content.
      sx={isScroller ? { scrollbarGutter: "stable" } : undefined}
      {...props}
    >
      <Column
        // Scrolling, at least the card's height, so content that fills the card still does; otherwise the card's height exactly.
        flex={isScroller ? undefined : 1}
        flexShrink={isScroller ? 0 : undefined}
        minHeight={isScroller ? "100%" : 0}
      >
        {children}
      </Column>
    </Column>
  );
};
